import uPlot from "uplot";

import { zoom } from "./plugins/zoom";
import { preset } from "./plugins/preset";
import { debug } from "./plugins/debug";
import { dataSync } from "./plugins/dataSync";
import { resize } from "./plugins/resize";

import { parseJsonOrDefault } from "../utils/parseJsonOrDefault";

function generateId(chart) {
  return chart.id + "__chart";
}

// Identity of the chart's series: what it is drawing and what the legend prints
// alongside each value. Everything else can be updated in place.
function seriesKey($chart) {
  return `${$chart.dataset.labels}|${$chart.dataset.valueMeta}`;
}

export const Chart = {
  mounted() {
    this.build();
  },

  // `data`, `unit` and `selected` are swapped in place by the dataSync plugin,
  // but uPlot has no API for changing the series list — labels and value_meta
  // are baked into the instance at construction. So a change to either has to
  // rebuild, or the legend keeps captioning points with the previous query's
  // labels and counts.
  updated() {
    if (this.seriesKey === seriesKey(this.el)) return;

    this.uplot?.destroy();
    this.build();
  },

  build() {
    const $chart = this.el;

    const data = parseJsonOrDefault($chart.dataset.data, []);
    const labels = parseJsonOrDefault($chart.dataset.labels, []);
    const preset_id = $chart.dataset.preset;
    const unit = $chart.dataset.unit;
    const selectedIndex =
      $chart.dataset.selected !== undefined ? parseInt($chart.dataset.selected) : null;
    const title = $chart.dataset.title || "";
    const id = generateId($chart);

    // Parse y-axis scale options
    const scale_y_min = parseJsonOrDefault($chart.dataset.scaleYMin, null);
    const scale_y_max = parseJsonOrDefault($chart.dataset.scaleYMax, null);
    const scale_y_max_lower_bound = parseJsonOrDefault($chart.dataset.scaleYMaxLowerBound, null);
    const axis_y_integers_only = parseJsonOrDefault($chart.dataset.axisYIntegersOnly, false);
    const y_axis_scale = $chart.dataset.yAxisScale || "linear";
    const y_axis_label = $chart.dataset.yAxisLabel || null;
    const value_meta = parseJsonOrDefault($chart.dataset.valueMeta, null);

    this.target = $chart.dataset.target === "" ? null : $chart.dataset.target;

    const opts = {
      title,
      id,
      plugins: [
        dataSync($chart),
        preset(preset_id, {
          labels,
          data,
          unit,
          onSelectedIndex: this.onSelectedIndex.bind(this),
          selectedIndex,
          scale_y_min,
          scale_y_max,
          scale_y_max_lower_bound,
          axis_y_integers_only,
          y_axis_scale,
          y_axis_label,
          value_meta,
        }),
        // resize must run after preset: presets ship hardcoded width/height
        // defaults that would otherwise clobber our container-derived size.
        resize($chart),
        zoom(false),
        window.enable_chart_debug ? debug() : undefined,
      ].filter((plugin) => plugin),
    };

    this.uplot = new uPlot(opts, data, $chart);
    this.seriesKey = seriesKey($chart);
  },

  onSelectedIndex(selected, timestamp, eventName = "graph_value_selected") {
    if (this.target) {
      this.pushEventTo(this.target, eventName, { selected, timestamp });
      return;
    }

    this.pushEvent(eventName, { selected, timestamp });
  },

  destroyed() {
    if (this.uplot) {
      this.uplot.destroy();
    }
  },
};
